import {
  createStyles,
  TextField,
  Typography,
  type WithStyles,
  withStyles,
} from "@material-ui/core";
import { useTranslations } from "adapters/i18n";
import SelectDropdown from "components/SelectDropdown";
import React, { type ChangeEvent } from "react";
import type { CategoryFilterFormState } from "../category.types";
import { useFilterOptions } from "../hooks/useFilterOptions";
import {
  CategoryStatusFilter,
  WeightGainCurveFilter,
} from "../utils/category.utils";

type CategoryFilterFormProps = {
  filterForm: CategoryFilterFormState;
  onChangeFilter: <T extends keyof CategoryFilterFormState>(
    field: T,
    value: CategoryFilterFormState[T]
  ) => void;
  showWeightGainCurveFilter: boolean;
} & WithStyles<typeof styles>;

function CategoryFilterForm({
  classes,
  filterForm,
  onChangeFilter,
  showWeightGainCurveFilter,
}: CategoryFilterFormProps) {
  const { t } = useTranslations();

  const { statusOptions, weightGainCurveOptions } = useFilterOptions();

  const handleChangeSearch = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeFilter("searchText", event.target.value);
  };

  const handleChangeStatus = (value: CategoryStatusFilter) => {
    onChangeFilter("categoryStatus", value);
  };

  const handleChangeWeightGainCurve = (value: WeightGainCurveFilter) => {
    onChangeFilter("weightGainCurveFilter", value);
  };

  return (
    <>
      <Typography className={classes.header} gutterBottom={true}>
        {t("animalCategory:farm-categories-information")}
      </Typography>
      <div className={classes.form} data-testid="category-filter-form">
        <TextField
          autoFocus={true}
          className={classes.searchField}
          data-testid="search-filter-field"
          inputProps={{ style: { paddingBottom: "12px" } }}
          label={t("animalCategory:search-categories")}
          onChange={handleChangeSearch}
          value={filterForm.searchText}
        />
        <div className={classes.selectInput}>
          <SelectDropdown
            data-testid="category-status-select"
            id="category-status-select"
            label={{ text: t("animalCategory:category-status") }}
            onChange={handleChangeStatus}
            options={statusOptions}
            value={filterForm.categoryStatus}
          />
        </div>
        {showWeightGainCurveFilter && (
          <div className={classes.selectInput}>
            <SelectDropdown
              data-testid="weight-gain-curve-select"
              id="weight-gain-curve-select"
              label={{ text: t("livestock:weight-gain-curve") }}
              options={weightGainCurveOptions}
              value={filterForm.weightGainCurveFilter}
              onChange={handleChangeWeightGainCurve}
            />
          </div>
        )}
      </div>
    </>
  );
}

const styles = () =>
  createStyles({
    header: {
      color: "#333333",
      fontSize: "20px",
      fontStretch: "medium",
      fontStyle: "medium",
      fontWeight: "bold",
      letterSpacing: "medium",
      lineHeight: "medium",
      marginTop: "40px",
    },
    form: {
      marginTop: "20px",
      display: "flex",
      flexDirection: "row",
    },
    searchField: {
      width: "400px",
    },
    selectInput: {
      maxWidth: "200px",
      width: "100%",
      marginLeft: 32,
    },
  });

export default withStyles(styles)(CategoryFilterForm);
